import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Star as LucideStar, ChevronsLeft, ChevronsRight } from "lucide-react";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const ORANGE = "#fcb14a";
const BLUE = "#00b3ff"; // azul intenso XPHub

const PLATFORM_ICONS = {
  pc: "/icons/windows.png",
  playstation: "/icons/playstation.png",
  xbox: "/icons/xbox.png",
  nintendo: "/icons/nintendo.png",
  ios: "/icons/ios.png",
  android: "/icons/android.png",
  mac: "/icons/apple.png",
  linux: "/icons/linux.png",
  web: "/icons/web.svg",
  steam: "/icons/steam.svg",
  epic: "/icons/epic.svg",
  stadia: "/icons/stadia.svg",
};

function formatRelease(dateStr) {
  if (!dateStr) return "Próximamente";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "Próximamente";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function useNormalizedGames(games) {
  return useMemo(() => {
    const g = games;
    if (Array.isArray(g)) return g;
    if (g && typeof g === "object") {
      if (Array.isArray(g.items)) return g.items;
      if (Array.isArray(g.results)) return g.results;
      if (Array.isArray(g.games)) return g.games;
      if (Array.isArray(g.data)) return g.data;
      for (const v of Object.values(g)) if (Array.isArray(v)) return v;
    }
    return [];
  }, [games]);
}

function ArrowButton({ onClick, dir = "left", disabled = false }) {
  const isLeft = dir === "left";

  return (
    <button
      type="button"
      aria-label={isLeft ? "Anterior" : "Siguiente"}
      className={`game-slider-arrow game-slider-arrow--${dir}`}
      onClick={onClick}
      disabled={disabled}
    >
      {isLeft ? (
        <ChevronsLeft size={28} strokeWidth={2.5} color={BLUE} />
      ) : (
        <ChevronsRight size={28} strokeWidth={2.5} color={BLUE} />
      )}
    </button>
  );
}

export default function GameSlider({
  title,
  games,
  loading = false,
  onClick,
  showPlayers = false,
  isLatest = false,
  viewAllTo,
  rightActionAsIcon = true,
  rightActionLabel = "Ver más >>",
  leftIcon,
  imageOnly = false,
}) {
  const normalizedGames = useNormalizedGames(games);
  const trackRef = useRef(null);
  const pointerStartRef = useRef(null);
  const suppressClickRef = useRef(false);
  const animationFrameRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollPrev: false,
    canScrollNext: false,
    progress: 0,
  });

  const updateScrollState = useCallback(() => {
    const node = trackRef.current;
    if (!node) return;

    const maxScrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
    const nextProgress = maxScrollLeft > 0 ? node.scrollLeft / maxScrollLeft : 1;

    setScrollState({
      canScrollPrev: node.scrollLeft > 2,
      canScrollNext: node.scrollLeft < maxScrollLeft - 2,
      progress: Math.min(1, Math.max(0, nextProgress)),
    });
  }, []);

  const requestScrollStateUpdate = useCallback(() => {
    if (animationFrameRef.current) return;
    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      updateScrollState();
    });
  }, [updateScrollState]);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);

    return () => {
      window.removeEventListener("resize", updateScrollState);
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [normalizedGames.length, updateScrollState]);

  const scrollByPage = useCallback((direction) => {
    const node = trackRef.current;
    if (!node) return;

    const delta = node.clientWidth * 0.86 * direction;
    node.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  const handlePointerDown = useCallback((event) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    suppressClickRef.current = false;
  }, []);

  const handlePointerMove = useCallback((event) => {
    const start = pointerStartRef.current;
    if (!start) return;

    const deltaX = Math.abs(event.clientX - start.x);
    const deltaY = Math.abs(event.clientY - start.y);
    if (deltaX > 8 || deltaY > 8) {
      suppressClickRef.current = true;
    }
  }, []);

  const handleCardClick = useCallback(
    (game) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      onClick?.(game);
    },
    [onClick]
  );

  return (
    <section className="slider-section" aria-label={title}>
      <div
        className="section-header"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}
      >
        {viewAllTo ? (
          <Link to={viewAllTo} className="section-title-link" style={{ textDecoration: "none" }}>
            <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {leftIcon && <span aria-hidden="true">{leftIcon}</span>}
              {title}
            </h2>
          </Link>
        ) : (
          <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {leftIcon && <span aria-hidden="true">{leftIcon}</span>}
            {title}
          </h2>
        )}

{viewAllTo && (
  <Link
    to={viewAllTo}
    className="view-all-link icon-link"
    aria-label="Ver más"
    title="Ver más"
  >
{rightActionAsIcon ? <ChevronDown size={18} className="plus-icon" /> : rightActionLabel}
  </Link>
)}


      </div>

      {loading ? (
        <div className={`game-slider-loading${imageOnly ? " game-slider-loading--poster" : ""}`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="game-slider-skeleton" borderRadius={10} />
          ))}
        </div>
      ) : normalizedGames.length === 0 ? (
        <div className="empty-block" style={{ color: "#bfbfbf", padding: "8px 0" }}>
          No hay juegos para mostrar.
        </div>
      ) : (
        <div className="game-slider-shell">
          <ArrowButton
            dir="left"
            onClick={() => scrollByPage(-1)}
            disabled={!scrollState.canScrollPrev}
          />

          <div
            ref={trackRef}
            className={`game-slider-track ${
              imageOnly ? "game-slider-track--poster" : "game-slider-track--standard"
            }`}
            onScroll={requestScrollStateUpdate}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
          >
            {normalizedGames.map((game) => {
              const platforms = Array.isArray(game.parent_platforms) ? game.parent_platforms : [];

              let metaNode = "—";
              if (!imageOnly) {
                if (isLatest) {
                  metaNode = <span>📅 {formatRelease(game.released)}</span>;
                } else if (showPlayers && typeof game.players === "number") {
                  metaNode = <span>👥 {Number(game.players).toLocaleString("es-ES")}</span>;
                } else if (typeof game.rating === "number") {
                  metaNode = (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <span>{game.rating.toFixed(1)}</span>
                      <LucideStar size={14} color={ORANGE} fill={ORANGE} />
                    </span>
                  );
                } else if (showPlayers) {
                  metaNode = <span>⭐ N/D</span>;
                }
              }

              return (
                <div className="game-slider-item" key={game.id || game._id || game.slug || game.name}>
                  <motion.div
                    className={`game-card${imageOnly ? " game-card--poster-only" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCardClick(game)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onClick?.(game);
                      }
                    }}
                    title={game.name}
                    whileHover={{ scale: 1.04, transition: { type: "spring", duration: 0.2, bounce: 0 } }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <img
                      src={game.background_image || game.image || "/images/placeholder.jpg"}
                      alt={game.name}
                      className={`game-card-img${imageOnly ? " game-card-img--poster-only" : ""}`}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />

                    {!imageOnly && (
                      <div className="game-card-content">
                        <div className="platform-icons">
                          {platforms.length > 0
                            ? platforms.map(({ platform }) => {
                                const slug = (platform?.slug || "").toLowerCase();
                                const icon = PLATFORM_ICONS[slug];
                                return icon ? <img key={slug} src={icon} alt={slug} /> : null;
                              })
                            : showPlayers
                            ? <img src={PLATFORM_ICONS.steam} alt="steam" />
                            : null}
                        </div>

                        <div className="game-card-title">
                          {game.name} {isLatest && "🔥"}
                        </div>

                        <div className="reactions">{metaNode}</div>
                      </div>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </div>

          <ArrowButton
            dir="right"
            onClick={() => scrollByPage(1)}
            disabled={!scrollState.canScrollNext}
          />

          <div className="game-slider-progress" aria-hidden="true">
            <div
              className="game-slider-progress__bar"
              style={{ transform: `scaleX(${scrollState.progress})` }}
            />
          </div>
        </div>
      )}
    </section>
  );
}
